import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Module } from '../../../ts/Module';
import { IUpdateServerWelcomeChannelInfoResult, UpdateServerWelcomeChannelInfoRequest } from '../../../ts/ServerApi/requests/UpdateServerWelcomeChannelInfoRequest';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { ErrorState } from '../../../ts/services/ErrorState';
import { ServerService } from '../../../ts/services/ServerService';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../../directives/tbSettingsCoordinator';

export interface CustomizationSettingsCtrlProps {
  setCancel: (cancelFn: () => void) => void;
  setIsBusy: (isBusyFn: () => boolean) => void;
  setSave: (saveFn: () => void) => void;
  setIsFormDirty: (isFormModified: boolean, triggerAngularRefresh?: boolean) => void;
  setIsFormValid: (isValid: boolean) => void;
  sendUpdateRequest: (defaultBannerEnabled: boolean, serverFooter: string) => ng.IPromise<IUpdateServerWelcomeChannelInfoResult>;
  setOpenNavigationDialog: (callback: Function) => void;
}

interface CustomizationSettingsCtrlScope extends ISettingsCoordinatorCtrlScope {
  props: CustomizationSettingsCtrlProps;
}

export class CustomizationSettingsCtrl {

  private cancelFn: () => void = () => {};
  private isBusyFn: () => boolean = () => false;
  private saveFn: () => ng.IPromise<any>;
  private openNavigationDialog: (successCallback: Function) => void;
  private formController: any;  // Just holds the $valid property used by the settingsCoordinator
  private isFormModified = false;

  public static $inject = [
    '$scope',
    'SettingsFormService',
    'ServerService',
    'ErrorState',
    'BrowserTitleService'
  ];

  constructor(
    private $scope: CustomizationSettingsCtrlScope,
    private formService: SettingsFormService,
    private serverService: ServerService,
    private ErrorState: ErrorState,
            browserTitleService: BrowserTitleService
  ) {
    this.formController = { $valid: true };
    this.redirectIfFeatureFlagOff();
    this.setupAngularForm();
    this.setupReactProps();
    this.setupNavigationConfirmationDialogs();

    browserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: Messages.serverSettings_customization()});
  }

  private redirectIfFeatureFlagOff() {
    if (!FeatureFlags.serverWelcomeChannelEnabled) {
      this.ErrorState.go({
        detailsLocKey: 'pageNotFoundHeader',
        redirectLocKey: 'backToContent'
      });
    }
  }

  private setupAngularForm() {
    this.$scope.localForm = {
      formController: this.formController,
      cancel: () => this.cancelFn(),
      isBusy: () => this.isBusyFn(),
      isFormUnchanged: () => !this.isFormModified,
      save: () => this.saveFn(),
    };

    this.$scope.sharedForm = this.formService;
    this.$scope.sharedForm.registerFormStateByName(this.$scope.localForm, 'customizationSettingsForm');
  }

  private setupReactProps() {
    this.$scope.props = {
      setSave: this.setSave.bind(this),
      setCancel: this.setCancel.bind(this),
      setIsBusy: this.setIsBusy.bind(this),
      setIsFormDirty: this.setIsFormModified.bind(this),
      setIsFormValid: this.setIsFormValid.bind(this),
      setOpenNavigationDialog: this.setOpenNavigationDialog.bind(this),
      sendUpdateRequest: this.sendUpdateServerWelcomeChannelInfoRequest.bind(this)
    };
  }

  private setupNavigationConfirmationDialogs() {
    // Handles the close tab / window usecases
    window.onbeforeunload = () => {
      return this.isFormModified ? '' : undefined;
    };
    // Handles navigating to another page/history with Angular router
    const deregisterListener = this.$scope.$on('$locationChangeStart', (event: any, newUrl: any) => {
      if (this.isFormModified) {
        event.preventDefault();
        this.openNavigationDialog(() => {
          deregisterListener();
          window.location = newUrl;
        });
      }
    });
  }

  public sendUpdateServerWelcomeChannelInfoRequest(defaultBannerEnabled: boolean, serverFooter: string): ng.IPromise<IUpdateServerWelcomeChannelInfoResult> {
    return this.serverService.sendRequest(
      new UpdateServerWelcomeChannelInfoRequest({
        welcomeChannelEnabledForServer: defaultBannerEnabled,
        welcomeChannelServerFooter: serverFooter
      })
    );
  }

  public setCancel(cancelFn: () => void) {
    this.cancelFn = cancelFn;
  }

  public setIsBusy(isBusyFn: () => boolean) {
    this.isBusyFn = isBusyFn;
  }

  public setSave(saveFn: () => ng.IPromise<any>) {
    this.saveFn = saveFn;
  }

  /**
   * triggerAngularRefresh: For certain functions like save/cancel, the Angular Settings Ctrl
   * already calls $scope.digest, so calling $apply would throw an error. It's called manually
   * here for UI changes that Angular is not aware of, eg all React component changes.
   */
  public setIsFormModified(isFormModified: boolean) {
    this.isFormModified = isFormModified;
    if (!this.$scope.$root.$$phase) {
      this.$scope.$apply(); // Manually tell angular to refresh the setting scope.
    }
  }

  public setIsFormValid(isFormValid: boolean) {
    this.formController.$valid = isFormValid;
  }

  /**
   * The dialog is a shared-widget. Since it's React, delegate the dialog handling
   * to the Customization Settings component.
   */
  public setOpenNavigationDialog(fn: () => void) {
    this.openNavigationDialog = fn;
  }
}

Module.instance().controller('CustomizationSettingsCtrl', CustomizationSettingsCtrl);
